import { api } from './api';
import { currentLang } from './i18n';
import { showToast } from './toast';
import {
  declineReviewCloseProposal,
  deleteSubtaskUI,
  dismissHold,
  editGoalDeadline,
  editSubtaskDeadline,
  GOAL_COMPLETION_EXIT_MS,
  inferAiAction,
  saveGoalDod,
  saveGoalDodRows,
  saveGoalTitle,
  selectCanonicalJudgmentOption,
  answerGoalActor,
  toggleDod,
  toggleGoalDone,
  toggleSubtask,
} from './goals-actions';
import { cachedAllGoals, doneGoalsHasMore, removeCachedGoal, userTimezone } from './state';
import { loadAllGoals } from './upload';
import { clearSearch } from './filters';
import { switchMainTab } from './navigation';
import { closeWorkDetail, finishWorkListChange, openWorkDetail, reloadWorkList, renderWorkList, revealOpenWorkRow, startWorkListChange, withWorkListChange, workListState } from './work-list';
import type { WorkListChange } from './work-list-pending';
import { detailTargetFor, todayDoneLine } from './work-detail-view';
import { goalCards, isSelectable, searchResultRange, type WorkItem } from './work-list-view';
import type { WorkListAction } from './components/work-list';

// #2749: what the Web list's buttons do. Each write uses the same endpoint as
// the goal cards; the list is read again from the server afterwards.
// #3549: a button that changes its row shows the change at once
// (withWorkListChange); the save and the read after it run behind it, and a
// failed save puts the row back.

function en() {
  return currentLang === 'en';
}

function openRow(key: string) {
  const item = workListState.list?.items.find((row) => row.key === key);
  if (!item) return;
  const goal = cachedAllGoals.find((g) => g.id === item.goal_id) ?? null;
  void openWorkDetail(detailTargetFor(item, goal));
}

function openGoal(goalId: string) {
  // The goal's own row, when it has one, is marked as the open one; else its first row.
  const rows = (workListState.list?.items ?? []).filter((item) => item.goal_id === goalId && !item.candidate_id);
  const row = rows.find((item) => !item.subtask_id) ?? rows[0];
  return openWorkDetail({ type: 'goal', goalId, key: row?.key ?? null });
}

/** A task opened from its goal's detail; closing it goes back to the goal. */
function openTask(goalId: string, subtaskId: string) {
  const row = workListState.list?.items.find((item) => item.subtask_id === subtaskId);
  void openWorkDetail({ type: 'task', goalId, subtaskId, key: row?.key ?? null, fromGoal: true });
}

function closeDetail() {
  const target = workListState.detail;
  if (target?.type === 'task' && target.fromGoal) {
    void openGoal(target.goalId);
    return;
  }
  closeWorkDetail();
}

/** Something the goal detail's box says about the goal; it reaches the goal's next review. */
async function sendGoalFeedback(goalId: string, text: string) {
  try {
    await api(`/api/goals/${encodeURIComponent(goalId)}/feedback`, { method: 'POST', json: { text } });
    showToast(en() ? 'Sent. It will be reflected in the next review.' : 'お伝えしました。次の見直しに反映します。', 'success');
  } catch {
    showToast(en() ? 'Could not send.' : '送れませんでした。', 'error');
  }
  await refreshAll();
}

const ACHIEVEMENT_MS = 6000;
let achievementTimer: number | undefined;

function hideAchievement() {
  window.clearTimeout(achievementTimer);
  if (!workListState.achievement) return;
  const host = document.getElementById('workAchievement');
  const hadFocus = !!host?.contains(document.activeElement);
  workListState.achievement = null;
  renderWorkList();
  if (hadFocus) (document.querySelector('#todayList .work-title') as HTMLElement | null)?.focus();
}

/** #2749: the achievement card replaces the toast once a goal is completed. */
function showAchievement(goalId: string, title: string) {
  const done = cachedAllGoals.filter((goal) => goal.status === 'done');
  const next = (workListState.list?.items ?? []).find((item) => item.card && item.goal_id !== goalId);
  workListState.achievement = {
    goalId,
    title,
    count: todayDoneLine(done, goalId, doneGoalsHasMore, en() ? 'en' : 'ja', userTimezone),
    next: next ? { goalId: next.goal_id, key: next.key, title: next.title } : null,
  };
  renderWorkList();
  // Stays while the user is reading it with the mouse or the keyboard.
  const schedule = () => {
    window.clearTimeout(achievementTimer);
    achievementTimer = window.setTimeout(() => {
      const host = document.getElementById('workAchievement');
      if (host && (host.matches(':hover') || host.contains(document.activeElement))) schedule();
      else hideAchievement();
    }, ACHIEVEMENT_MS);
  };
  schedule();
}

async function setExecutor(subtaskId: string, executor: 'human' | 'ai') {
  const st = cachedAllGoals.flatMap((goal) => goal.subtasks || []).find((item) => item.id === subtaskId);
  try {
    await api(`/api/subtasks/${encodeURIComponent(subtaskId)}`, {
      method: 'PATCH',
      json: { executor, ai_action: executor === 'ai' ? inferAiAction(st?.title ?? '') : null },
    });
  } catch {
    showToast(en() ? 'Could not update.' : '更新できませんでした。', 'error');
  }
  await refreshAll();
}

async function approve(subtaskId: string, resultContent: string | null): Promise<boolean> {
  try {
    await api(`/api/subtasks/${encodeURIComponent(subtaskId)}/approve`, {
      method: 'POST',
      json: resultContent === null ? {} : { result_content: resultContent },
    });
    showToast(en() ? 'Approved.' : '承認しました。', 'success');
    return true;
  } catch (err) {
    const message = err instanceof Error ? err.message : '';
    showToast(message.includes('no longer actionable') || message.includes('parent goal is closed')
      ? (en() ? 'This task is no longer actionable because the parent goal is closed.' : '親ゴールが閉じているため、このタスクは承認できません。')
      : (en() ? 'Could not approve.' : '承認できませんでした。'), 'error');
    return false;
  }
}

/** On an AI result it asks for a redo; otherwise it reaches the goal's next review. */
async function sendFeedback(detail: { goalId: string; subtaskId: string; taskTitle: string; text: string; redo: boolean }) {
  try {
    if (detail.redo) {
      await api(`/api/subtasks/${encodeURIComponent(detail.subtaskId)}/reject`, { method: 'POST', json: { feedback: detail.text } });
      showToast(en() ? 'Asked the AI to redo it.' : 'AIにやり直しをお願いしました。', 'success');
    } else {
      const about = en() ? `About "${detail.taskTitle}": ` : `「${detail.taskTitle}」について：`;
      await api(`/api/goals/${encodeURIComponent(detail.goalId)}/feedback`, { method: 'POST', json: { text: about + detail.text } });
      showToast(en() ? 'Sent. It will be reflected in the next review.' : 'お伝えしました。次の見直しに反映します。', 'success');
    }
  } catch {
    showToast(en() ? 'Could not send.' : '送れませんでした。', 'error');
  }
  await refreshAll();
}

async function decideCandidate(candidateId: string, accept: boolean) {
  const id = encodeURIComponent(candidateId);
  if (accept) {
    await api(`/api/goals/candidates/${id}/accept`, { method: 'POST' });
  } else {
    await api(`/api/goals/candidates/${id}/dismiss`, {
      method: 'POST',
      json: { reason_summary: en() ? 'Dismissed from the web list' : 'Webの一覧から見送り' },
    });
  }
}

async function decideProposal(goalId: string, proposalId: string, accept: boolean) {
  await api(`/api/goals/${encodeURIComponent(goalId)}/proposals/${encodeURIComponent(proposalId)}/action`, {
    method: 'POST',
    json: { action: accept ? 'accept' : 'reject' },
  });
}

/**
 * #1929 S4: answer a held thread message: the goals it continues, a new goal
 * (Zenmetry reads the message again and Jev makes the goal), or not needed.
 */
async function answerGoalLink(holdId: string, answer: { goalIds: string[] } | 'new' | 'dismiss') {
  if (typeof answer === 'object' && answer.goalIds.length === 0) {
    showToast(en() ? 'Pick the goals it continues first.' : '先に続きにあたるゴールを選んでください。');
    return;
  }
  if (answer === 'dismiss' && !window.confirm(en() ? 'Mark this message not needed?' : 'このメッセージを不要にしますか？')) return;
  await withWorkListChange([{ type: 'leave', by: 'hold', id: holdId }], () => saveGoalLink(holdId, answer), GOAL_COMPLETION_EXIT_MS);
  await refreshAll();
}

async function saveGoalLink(holdId: string, answer: { goalIds: string[] } | 'new' | 'dismiss'): Promise<boolean> {
  const path = `/api/intake/admission-candidates/${encodeURIComponent(holdId)}`;
  try {
    if (answer === 'new') {
      await api(`${path}/new-goal`, { method: 'POST', json: {} });
      showToast(en() ? 'Made it a new goal.' : '新しいゴールにしました。', 'success');
    } else if (answer === 'dismiss') {
      await api(`${path}/dismiss`, { method: 'POST', json: { reason_summary: en() ? 'Dismissed from the web list' : 'Webの一覧から不要' } });
      showToast(en() ? 'Marked it not needed.' : '不要にしました。', 'success');
    } else {
      await api(`${path}/link`, { method: 'POST', json: { goal_ids: answer.goalIds } });
      showToast(en() ? 'Added the message to the chosen goals.' : '選んだゴールの続きとして記録しました。', 'success');
    }
    return true;
  } catch {
    showToast(answer === 'new'
      ? (en() ? 'Could not make it a goal.' : 'ゴールにできませんでした。')
      : (en() ? 'Could not update.' : '更新できませんでした。'), 'error');
    return false;
  }
}

/** Shows a change on the list at once and saves it with `send`; a failure is told and takes the change back. */
function saveWithChange(changes: WorkListChange[], send: () => Promise<unknown>, failed: () => string): Promise<boolean> {
  return withWorkListChange(changes, async () => {
    try {
      await send();
      return true;
    } catch {
      showToast(failed(), 'error');
      return false;
    }
  }, GOAL_COMPLETION_EXIT_MS);
}

async function refreshAll() {
  await loadAllGoals({ silent: true }).catch(() => {});
}

/** Approve or turn down every chosen goal candidate and completion proposal. */
/** Set while 「まとめて…にする」 (#3064) is saving, so a second press does not save twice. */
let savingDeadlines = false;

/** How a goal candidate or completion proposal leaves once answered: a completed goal takes all its rows. */
function answeredRow(item: WorkItem, accept: boolean): WorkListChange {
  if (item.candidate_id) return { type: 'leave', by: 'candidate', id: item.candidate_id };
  if (accept) return { type: 'leave', by: 'goal', id: item.goal_id };
  return { type: 'leave', by: 'completion', id: item.completion_id ?? '' };
}

async function runBulk(accept: boolean) {
  const items = (workListState.list?.items ?? []).filter((item) => isSelectable(item) && workListState.selected.has(item.key));
  if (items.length === 0) return;
  if (!accept && !window.confirm(en() ? `Mark ${items.length} items not needed?` : `${items.length}件を不要にしますか？`)) return;
  const completions = items.every((item) => item.completion_id !== null);
  // #3549: the chosen rows leave at once; the saves run one after another behind them.
  const runs = items.map((item) => {
    workListState.selected.delete(item.key);
    return { item, change: startWorkListChange([answeredRow(item, accept)], GOAL_COMPLETION_EXIT_MS) };
  });
  let failed = 0;
  for (const { item, change } of runs) {
    let saved = false;
    try {
      if (item.candidate_id) await decideCandidate(item.candidate_id, accept);
      else if (item.completion_id) await decideProposal(item.goal_id, item.completion_id, accept);
      saved = true;
    } catch {
      failed += 1;
    }
    finishWorkListChange(change, saved);
  }
  const done = items.length - failed;
  if (done > 0) {
    showToast(accept
      ? (completions
        ? (en() ? `Completed ${done}.` : `${done}件を完了にしました。`)
        : (en() ? `Made ${done} goals.` : `${done}件をゴールにしました。`))
      : (en() ? `Marked ${done} not needed.` : `${done}件を不要にしました。`), 'success');
  }
  if (failed > 0) showToast(en() ? `${failed} could not be updated.` : `${failed}件は更新できませんでした。`, 'error');
  await refreshAll();
}

/**
 * Yoshie 2026-10-29: the chosen goal cards completed, or dropped as not needed
 * (the goal is deleted, as from its detail), together. The cards leave at once;
 * the saves run one after another behind them.
 */
async function runCardBulk(done: boolean) {
  const cards = goalCards(workListState.list?.items ?? []).filter((card) => workListState.selected.has(card.key));
  if (cards.length === 0) return;
  if (!done && !window.confirm(en() ? `Mark ${cards.length} goals not needed? They are deleted.` : `${cards.length}件のゴールを不要にして削除しますか？`)) return;
  const runs = cards.map((card) => {
    workListState.selected.delete(card.key);
    return { card, change: startWorkListChange([{ type: 'leave', by: 'goal', id: card.goal_id }], GOAL_COMPLETION_EXIT_MS) };
  });
  workListState.selecting = false;
  let failed = 0;
  for (const { card, change } of runs) {
    let saved = false;
    try {
      if (done) {
        saved = await toggleGoalDone(card.goal_id, false, { quiet: true });
      } else {
        await api(`/api/goals/${encodeURIComponent(card.goal_id)}`, { method: 'DELETE' });
        removeCachedGoal(card.goal_id);
        saved = true;
      }
    } catch {
      saved = false;
    }
    if (!saved) failed += 1;
    finishWorkListChange(change, saved);
  }
  const ok = cards.length - failed;
  if (ok > 0) {
    showToast(done
      ? (en() ? `Completed ${ok} goals.` : `${ok}件のゴールを完了にしました。`)
      : (en() ? `Marked ${ok} goals not needed.` : `${ok}件のゴールを不要にしました。`), 'success');
  }
  if (failed > 0) showToast(en() ? `${failed} could not be updated.` : `${failed}件は更新できませんでした。`, 'error');
  await reloadWorkList();
}

export async function handleWorkListAction(detail: WorkListAction) {
  switch (detail.action) {
    case 'filter':
      workListState.filter = workListState.filter === detail.kind ? null : detail.kind;
      renderWorkList();
      return;
    case 'customer-filter':
      workListState.customerFilter = detail.domain;
      // #3805: a customer picked in search shows that customer's goals on every date.
      if (detail.fromSearch) {
        clearSearch();
        switchMainTab('all');
      }
      renderWorkList();
      return;
    case 'toggle-low':
      workListState.lowExpanded = !workListState.lowExpanded;
      renderWorkList();
      return;
    case 'select-cards':
      workListState.selecting = detail.on;
      if (!detail.on) workListState.selected.clear();
      renderWorkList();
      return;
    case 'card-bulk':
      if (detail.bulk === 'all') {
        for (const key of detail.keys ?? []) workListState.selected.add(key);
        renderWorkList();
      } else {
        await runCardBulk(detail.bulk === 'done');
      }
      return;
    case 'open':
      // Moving on from the achievement card (its 「次は」) puts it away.
      hideAchievement();
      openRow(detail.key);
      return;
    case 'open-goal':
      void openGoal(detail.goalId);
      return;
    case 'open-task':
      openTask(detail.goalId, detail.subtaskId);
      return;
    case 'close-detail':
      closeDetail();
      return;
    case 'search-open':
      // #2857: a search result opens the list's detail, beside the list.
      clearSearch();
      switchMainTab(searchResultRange(workListState.list, detail.goalId));
      if (detail.subtaskId) {
        const row = workListState.list?.items.find((item) => item.subtask_id === detail.subtaskId);
        await openWorkDetail({ type: 'task', goalId: detail.goalId, subtaskId: detail.subtaskId, key: row?.key ?? null });
      } else {
        await openGoal(detail.goalId);
      }
      await revealOpenWorkRow();
      return;
    case 'goal-title':
      await saveGoalTitle(detail.goalId, detail.title);
      return;
    case 'goal-conditions':
      await saveGoalDod(detail.goalId, detail.text);
      return;
    case 'goal-condition-rows': {
      const goal = cachedAllGoals.find((g) => g.id === detail.goalId);
      if (goal) await saveGoalDodRows(goal, detail.edits);
      return;
    }
    case 'goal-feedback':
      await sendGoalFeedback(detail.goalId, detail.text);
      return;
    case 'achievement-close':
      hideAchievement();
      return;
    case 'approve':
      await withWorkListChange([{ type: 'leave', by: 'subtask', id: detail.subtaskId }],
        () => approve(detail.subtaskId, detail.resultContent), GOAL_COMPLETION_EXIT_MS);
      await refreshAll();
      return;
    case 'feedback':
      await sendFeedback(detail);
      return;
    case 'complete-task':
      await withWorkListChange([{ type: 'check', id: detail.subtaskId }], () => toggleSubtask(detail.subtaskId, false));
      await reloadWorkList();
      return;
    case 'executor':
      await setExecutor(detail.subtaskId, detail.executor);
      return;
    case 'not-needed': {
      // The row leaves once the deletion is confirmed, before it is saved.
      let change: number | null = null;
      const deleted = await deleteSubtaskUI(detail.subtaskId, detail.title, () => {
        change = startWorkListChange([{ type: 'leave', by: 'subtask', id: detail.subtaskId }], GOAL_COMPLETION_EXIT_MS);
      });
      if (change === null) return;
      finishWorkListChange(change, deleted);
      await reloadWorkList();
      return;
    }
    case 'task-deadline':
      editSubtaskDeadline(detail.subtaskId, detail.element, detail.current);
      return;
    case 'task-condition':
      await toggleDod(detail.subtaskId, detail.index, detail.done);
      return;
    case 'select':
      if (detail.selected) workListState.selected.add(detail.key);
      else workListState.selected.delete(detail.key);
      renderWorkList();
      return;
    case 'bulk':
      if (detail.bulk === 'all') {
        for (const item of workListState.list?.items ?? []) {
          if (isSelectable(item)) workListState.selected.add(item.key);
        }
        renderWorkList();
      } else if (detail.bulk === 'clear') {
        workListState.selected.clear();
        renderWorkList();
      } else {
        await runBulk(detail.bulk === 'accept');
      }
      return;
    case 'check-step':
      await withWorkListChange([{ type: 'check', id: detail.subtaskId }], () => toggleSubtask(detail.subtaskId, false));
      await reloadWorkList();
      return;
    case 'undo-step':
      await toggleSubtask(detail.subtaskId, true);
      await reloadWorkList();
      return;
    case 'check-goal': {
      const title = cachedAllGoals.find((goal) => goal.id === detail.goalId)?.title ?? '';
      const goalId = detail.goalId;
      // The card leaves once its exit has played. Goals checked in a row each
      // play theirs, and a read already running does not bring them back.
      const done = await withWorkListChange([{ type: 'leave', by: 'goal', id: goalId }],
        () => toggleGoalDone(goalId, false, { quiet: true }), GOAL_COMPLETION_EXIT_MS);
      // #2857: the card shows as soon as the save is through; the list is read behind it.
      if (done) showAchievement(goalId, title);
      await reloadWorkList();
      return;
    }
    case 'goal-deadline':
      // #3064: 「その日付にする」 on a provisional deadline is the principal's own deadline.
      if (await saveWithChange([{ type: 'deadline', goalId: detail.goalId, day: detail.day }],
        () => api(`/api/goals/${encodeURIComponent(detail.goalId)}`, { method: 'PATCH', json: { deadline: detail.day, deadline_has_time: false } }),
        () => (en() ? 'Could not save.' : '記録できませんでした。'))) {
        showToast(en() ? 'Set the deadline.' : '期限を決めました。', 'success');
      }
      await refreshAll();
      return;
    case 'goal-deadline-bulk': {
      // #3064 (owner 2026-10-26): the passed provisional deadlines set together to the date offered.
      if (savingDeadlines) return;
      savingDeadlines = true;
      showToast(en() ? `Saving ${detail.goalIds.length} deadlines…` : `${detail.goalIds.length}件の期限を記録しています…`, 'info');
      // One at a time: on staging (2026-10-26) four at once saved only 25 of 74,
      // and the host answered 429 right after. A refused one is tried once more after a pause;
      // when that fails too the rest are left, and the folded row still offers them.
      const save = (goalId: string) => api(`/api/goals/${encodeURIComponent(goalId)}`, { method: 'PATCH', json: { deadline: detail.day, deadline_has_time: false } });
      let done = 0;
      for (const goalId of detail.goalIds) {
        try {
          await save(goalId);
        } catch {
          await new Promise((resolve) => window.setTimeout(resolve, 3000));
          try {
            await save(goalId);
          } catch {
            break;
          }
        }
        done += 1;
      }
      savingDeadlines = false;
      const left = detail.goalIds.length - done;
      if (done > 0) showToast(en() ? `Set ${done} deadlines.` : `${done}件の期限を決めました。`, 'success');
      if (left > 0) showToast(en() ? `${left} could not be saved yet. Press again to save them.` : `${left}件はまだ記録できていません。もう一度押すと記録します。`, 'error');
      await refreshAll();
      return;
    }
    case 'goal-deadline-pick':
      editGoalDeadline(detail.goalId, detail.element, detail.current, (day) => {
        // Saved already: the row shows the day until the list read after it has it.
        if (day) finishWorkListChange(startWorkListChange([{ type: 'deadline', goalId: detail.goalId, day }]), true);
        void reloadWorkList();
      });
      return;
    case 'choose':
      // #2857: the row shows the choice at once and the list is read once it is saved.
      await withWorkListChange([{ type: 'choose', key: detail.key, optionKey: detail.optionKey }],
        () => selectCanonicalJudgmentOption(detail.goalId, detail.optionKey, detail.optionValue, detail.token));
      await reloadWorkList();
      return;
    case 'choose-text':
      // #2951: the place typed on a place card.
      await withWorkListChange([{ type: 'choose', key: detail.key, optionKey: detail.optionKey }],
        () => selectCanonicalJudgmentOption(detail.goalId, detail.optionKey, detail.optionValue, detail.token, undefined, detail.text));
      await reloadWorkList();
      return;
    case 'actor-answer':
      // The row shows the answer at once, like a judgment choice (#2857).
      await withWorkListChange([{ type: 'choose', key: detail.key, optionKey: detail.optionKey }],
        () => answerGoalActor(detail.goalId, detail.conditionRef, detail.actor, detail.teammateUserId));
      await reloadWorkList();
      return;
    case 'goal-turn-change':
      // #3222: the note goes first, so the review reads it while the turn holds.
      try {
        await api(`/api/goals/${encodeURIComponent(detail.goalId)}/feedback`, { method: 'POST', json: { text: detail.text } });
        await api(`/api/goals/${encodeURIComponent(detail.goalId)}/turn`, { method: 'POST', json: { turn: detail.turn } });
        showToast(detail.turn === 'counterparty'
          ? (en() ? 'Moved to waiting.' : '対応待ちにしました。')
          : (en() ? 'Back to you. The next tasks will be rebuilt.' : 'あなたの番に戻しました。次にやることを組み直します。'), 'success');
      } catch {
        showToast(en() ? 'Could not save.' : '記録できませんでした。', 'error');
      }
      await refreshAll();
      return;
    case 'close-proposal': {
      // The proposal's row leaves either way; marking it done also checks the task.
      const proposal: WorkListChange = { type: 'leave', by: 'close_proposal', id: detail.subtaskId };
      if (detail.accept) {
        await withWorkListChange([proposal, { type: 'check', id: detail.subtaskId }], () => toggleSubtask(detail.subtaskId, false), GOAL_COMPLETION_EXIT_MS);
      } else {
        await withWorkListChange([proposal], () => declineReviewCloseProposal(detail.subtaskId), GOAL_COMPLETION_EXIT_MS);
      }
      await reloadWorkList();
      return;
    }
    case 'release-hold':
      // Owner 2026-10-28: the release goes to the AI, approved; the row waits on it.
      await withWorkListChange([{ type: 'leave', by: 'close_proposal', id: detail.subtaskId }],
        () => dismissHold(detail.goalId, detail.eventId), GOAL_COMPLETION_EXIT_MS);
      await reloadWorkList();
      return;
    case 'candidate':
      if (await saveWithChange([{ type: 'leave', by: 'candidate', id: detail.candidateId }],
        () => decideCandidate(detail.candidateId, detail.accept),
        () => (en() ? 'Could not update.' : '更新できませんでした。'))) {
        showToast(detail.accept
          ? (en() ? 'Made it a goal.' : 'ゴールにしました。')
          : (en() ? 'Dismissed the candidate.' : '候補を見送りました。'), 'success');
      }
      await refreshAll();
      return;
    case 'goal-link':
      await answerGoalLink(detail.holdId, { goalIds: detail.goalIds });
      return;
    case 'goal-link-new':
      await answerGoalLink(detail.holdId, 'new');
      return;
    case 'goal-link-dismiss':
      await answerGoalLink(detail.holdId, 'dismiss');
      return;
    case 'undo-goal-done':
      // #2985: put back a reply-wait goal the review completed.
      // The done goal is not among the cached goals, so the list reopens it directly.
      if (await saveWithChange([{ type: 'leave', by: 'goal', id: detail.goalId }],
        () => api(`/api/goals/${encodeURIComponent(detail.goalId)}`, { method: 'PATCH', json: { status: 'active' } }),
        () => (en() ? 'Could not update.' : '更新できませんでした。'))) {
        showToast(en() ? 'Put the goal back.' : 'ゴールを戻しました。', 'success');
      }
      await refreshAll();
      return;
    case 'reopen':
      if (await saveWithChange([{ type: 'leave', by: 'reopen', id: detail.proposalId }],
        () => decideProposal(detail.goalId, detail.proposalId, detail.accept),
        () => (en() ? 'Could not update.' : '更新できませんでした。'))) {
        showToast(detail.accept
          ? (en() ? 'Reopened the goal.' : 'ゴールを再開しました。')
          : (en() ? 'Kept the goal done.' : 'ゴールは完了のままにしました。'), 'success');
      }
      await refreshAll();
      return;
    case 'completion': {
      // 完了にする takes the goal's rows with it; まだ only the proposal's row.
      const title = cachedAllGoals.find((goal) => goal.id === detail.goalId)?.title ?? '';
      const change: WorkListChange = detail.accept
        ? { type: 'leave', by: 'goal', id: detail.goalId }
        : { type: 'leave', by: 'completion', id: detail.completionId };
      if (await saveWithChange([change],
        () => decideProposal(detail.goalId, detail.completionId, detail.accept),
        () => (en() ? 'Could not update.' : '更新できませんでした。'))) {
        // The card shows once the save is through; the goals are read behind it.
        if (detail.accept) showAchievement(detail.goalId, title);
        else showToast(en() ? 'Kept the goal open.' : 'ゴールを続けます。', 'success');
      }
      await refreshAll();
      return;
    }
  }
}
